import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useUser } from '@/hooks/useUser'
import useIsMobile from '@/hooks/useIsMobile'
import { signOut } from '@/lib/auth'
import { clearGuestSession } from '@/lib/guestStorage'
import { normalizeEmojis } from '@/lib/emojiUtils'
import { theme } from '@/lib/theme'
import {
  extractSettingsContent,
  getUserSettings,
  type UserSettings,
} from '@/types/UserSettings'
import Button from './ui/Button'
import Container from './ui/Container'
import Input from './ui/Input'
import Message from './ui/Message'
import { SettingsHelpProvider, SettingsHelpToggle, useSettingsHelp } from './helpers/SettingsHelp'

/**
 * Settings component for configuring application preferences.
 */
function SettingsContent() {
  const { helpEnabled } = useSettingsHelp()
  const { t, i18n } = useTranslation()

  const { colors } = useThemeMode()
  const { user, isLoading: isUserLoading } = useUser()
  const isMobile = useIsMobile()
  const [settings, setSettings] = useState<UserSettings>(getUserSettings())
  const [isLoading, setIsLoading] = useState(false)
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)

  // Helper function to get translated field labels and placeholders
  const getFieldTranslation = (
    fieldKey: string
  ): { label: string; placeholder: string; description: string } => {
    const mapping: Record<
      string,
      { label: string; placeholder: string; description: string }
    > = {
      openaiApiKey: {
        label: t('settings.apiKeys.openaiLabel'),
        placeholder: t('settings.apiKeys.openaiPlaceholder'),
        description: t('settings.apiKeys.openaiDescription'),
      },
      anthropicApiKey: {
        label: t('settings.apiKeys.anthropicLabel'),
        placeholder: t('settings.apiKeys.anthropicPlaceholder'),
        description: t('settings.apiKeys.anthropicDescription'),
      },
      googleApiKey: {
        label: t('settings.apiKeys.googleLabel'),
        placeholder: t('settings.apiKeys.googlePlaceholder'),
        description: t('settings.apiKeys.googleDescription'),
      },
      groqApiKey: {
        label: t('settings.apiKeys.groqLabel'),
        placeholder: t('settings.apiKeys.groqPlaceholder'),
        description: t('settings.apiKeys.groqDescription'),
      },
      openrouterApiKey: {
        label: t('settings.apiKeys.openrouterLabel'),
        placeholder: t('settings.apiKeys.openrouterPlaceholder'),
        description: t('settings.apiKeys.openrouterDescription'),
      },
      selectedOpenaiModel: {
        label: t('settings.models.selectedOpenaiLabel'),
        placeholder: '',
        description: t('settings.models.selectedOpenaiDescription'),
      },
      selectedAnthropicModel: {
        label: t('settings.models.selectedAnthropicLabel'),
        placeholder: '',
        description: t('settings.models.selectedAnthropicDescription'),
      },
      selectedGoogleModel: {
        label: t('settings.models.selectedGoogleLabel'),
        placeholder: '',
        description: t('settings.models.selectedGoogleDescription'),
      },
      selectedGroqModel: {
        label: t('settings.models.selectedGroqLabel'),
        placeholder: '',
        description: t('settings.models.selectedGroqDescription'),
      },
      selectedOpenrouterModel: {
        label: t('settings.models.selectedOpenrouterLabel'),
        placeholder: '',
        description: t('settings.models.selectedOpenrouterDescription'),
      },
      selectedProvider: {
        label: t('settings.selectedProvider'),
        placeholder: '',
        description: t('settings.selectedProviderDescription'),
      },
      webSearchEnabled: {
        label: t('settings.webSearchEnabled'),
        placeholder: '',
        description: t('settings.webSearchEnabledDescription'),
      },
      selectedThemeId: {
        label: t('settings.selectedThemeId'),
        placeholder: '',
        description: t('settings.selectedThemeIdDescription'),
      },
      selectedIdentityId: {
        label: t('settings.selectedIdentityId'),
        placeholder: '',
        description: t('settings.selectedIdentityIdDescription'),
      },
    }
    return (
      mapping[fieldKey] || { label: fieldKey, placeholder: '', description: '' }
    )
  }

  const handleSave = () => {
    setIsLoading(true)
    try {
      const content = extractSettingsContent(settings)
      localStorage.setItem('settings', JSON.stringify(content))
      setFeedbackMessage(t('settings.settingsSaved'))
    } catch (error) {
      setFeedbackMessage(t('settings.settingsError'))
      console.error('Failed to save settings:', error)
    }
    setIsLoading(false)
    setTimeout(() => {
      setFeedbackMessage(null)
    }, 2000)
  }

  const handleInputChange = (fieldKey: keyof UserSettings, value: string) => {
    setSettings((prev) => ({
      ...prev,
      [fieldKey]: {
        ...prev[fieldKey],
        content: value,
      },
    }))
  }

  const handleLogout = async () => {
    setIsLoading(true)
    try {
      const result = await signOut()
      if (result.error) {
        setFeedbackMessage(`${t('app.error')}: ${result.error}`)
        setIsLoading(false)
        setTimeout(() => {
          setFeedbackMessage(null)
        }, 3000)
        return
      }

      // Clear guest session if any
      clearGuestSession()

      // Reload the page to reset the app state
      window.location.reload()
    } catch (error) {
      setFeedbackMessage(t('settings.logoutError'))
      console.error('Logout error:', error)
      setIsLoading(false)
      setTimeout(() => {
        setFeedbackMessage(null)
      }, 3000)
    }
  }

  // Show loading state while checking authentication
  if (isUserLoading) {
    return (
      <Container style={{ overflow: 'auto', padding: theme.spacing.xl }}>
        <Message variant="loading">{t('app.loading')}</Message>
      </Container>
    )
  }

  // Show login prompt if user is not authenticated
  if (!user) {
    return (
      <Container
        style={{
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: theme.spacing.lg,
          gap: theme.spacing.md,
        }}
      >
        <div style={{ fontSize: '32px', fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔐')}</div>
        <div
          style={{
            color: colors.primaryText,
            fontSize: isMobile ? '14px' : theme.fontSize.md,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {t('settings.authRequired')}
        </div>
        <div
          style={{
            color: colors.secondaryText,
            fontSize: isMobile ? '12px' : theme.fontSize.sm,
            textAlign: 'center',
            maxWidth: '280px',
          }}
        >
          {t('settings.authRequiredMessage')}
        </div>
      </Container>
    )
  }

  const shortcuts = [
    { keys: ['C'], description: t('settings.shortcuts.openChat'), category: 'navigation' },
    { keys: ['F'], description: t('settings.shortcuts.openFileExplorer'), category: 'navigation' },
    { keys: ['S'], description: t('settings.shortcuts.openSettings'), category: 'navigation' },
    { keys: ['P'], description: t('settings.shortcuts.openProfile'), category: 'navigation' },
    { keys: ['R'], description: t('settings.shortcuts.openRepresentation'), category: 'navigation' },
    { keys: ['←', '→'], description: t('settings.shortcuts.selectFocusWindows'), category: 'window' },
    { keys: ['⌥', '←'], description: t('settings.shortcuts.swapWindowLeft'), category: 'window' },
    { keys: ['↑'], description: t('settings.shortcuts.maximizeWindow'), category: 'window' },
    { keys: ['⌥', '→'], description: t('settings.shortcuts.swapWindowRight'), category: 'window' },
    { keys: ['↓'], description: t('settings.shortcuts.minimizeWindow'), category: 'window' },
    { keys: ['Esc'], description: t('settings.shortcuts.closeWindow'), category: 'window' },
  ]

  const navigationShortcuts = shortcuts.filter(s => s.category === 'navigation')
  const windowShortcuts = shortcuts.filter(s => s.category === 'window')

  const KeyCap = ({ keyLabel }: { keyLabel: string }) => (
    <kbd
      style={{
        display: 'inline-block',
        padding: '4px 8px',
        fontSize: theme.fontSize.xs,
        fontWeight: '600',
        lineHeight: '1',
        color: colors.primaryText,
        backgroundColor: colors.secondaryBg,
        border: `1px solid ${colors.primaryBorder}`,
        borderRadius: '4px',
        boxShadow: `0 2px 0 ${colors.primaryBorder}, 0 3px 1px rgba(0, 0, 0, 0.1)`,
        fontFamily: theme.fontFamily.mono,
        textTransform: 'uppercase',
        minWidth: '28px',
        textAlign: 'center',
      }}
    >
      {keyLabel}
    </kbd>
  )

  return (
    <Container style={{ overflow: 'auto', padding: isMobile ? theme.spacing.sm : theme.spacing.xl, position: 'relative' }}>

      {/* Header with buttons */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.sm,
        }}
      >
        <h2
          style={{
            fontSize: isMobile ? theme.fontSize.md : theme.fontSize.lg,
            fontWeight: 'bold',
            margin: 0,
          }}
        >
          {t('settings.title')}
        </h2>
        <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
          <SettingsHelpToggle />
          {/* Logout button hidden on mobile - use MobileNav menu instead */}
          {!isMobile && (
            <Button
              onClick={() => setShowLogoutConfirm(true)}
              disabled={isLoading}
              isLoading={isLoading}
              variant="destructive"
              size="sm"
            >
              {t('settings.logoutButton')}
            </Button>
          )}
        </div>
      </div>

      {/* Help Banner */}
      {helpEnabled && (
        <div
          style={{
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
            backgroundColor: colors.primaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: theme.borderRadius.sm,
            color: colors.secondaryText,
            fontSize: theme.fontSize.sm,
            lineHeight: '1.6',
            marginBottom: theme.spacing.lg,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing.xs,
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '16px',
              height: '16px',
              minWidth: '16px',
              borderRadius: '50%',
              backgroundColor: colors.primaryAccent,
              color: colors.primaryBg,
              fontSize: '10px',
              fontWeight: 'bold',
              fontFamily: 'monospace',
              marginTop: '2px',
              marginRight: '4px',
            }}
          >
            ?
          </span>
          <span>
            {t('help.settings.banner')}
          </span>
        </div>
      )}

      {/* Keyboard Shortcuts Modal - hide on mobile since they don't apply */}
      {showShortcuts && !isMobile && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
            padding: theme.spacing.xl,
          }}
          onClick={() => setShowShortcuts(false)}
        >
          <div
            style={{
              backgroundColor: colors.primaryBg,
              borderRadius: '8px',
              border: `1px solid ${colors.primaryBorder}`,
              padding: theme.spacing.xl,
              maxWidth: '900px',
              width: '90%',
              maxHeight: '70%',
              overflowY: 'auto',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowShortcuts(false)}
              style={{
                position: 'absolute',
                top: theme.spacing.md,
                right: theme.spacing.md,
                backgroundColor: 'transparent',
                border: 'none',
                color: colors.secondaryText,
                cursor: 'pointer',
                fontSize: '24px',
                padding: '4px',
                lineHeight: '1',
              }}
            >
              ✕
            </button>

            <h3
              style={{
                fontSize: theme.fontSize.lg,
                fontWeight: '600',
                marginBottom: theme.spacing.lg,
                color: colors.primaryText,
              }}
            >
              ⌨️ {t('settings.keyboardShortcuts')}
            </h3>

            {/* Navigation Shortcuts */}
            <div style={{ marginBottom: theme.spacing.lg }}>
              <h4
                style={{
                  fontSize: theme.fontSize.sm,
                  fontWeight: '600',
                  marginBottom: theme.spacing.sm,
                  color: colors.secondaryText,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {t('settings.navigation')}
              </h4>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: theme.spacing.md,
                }}
              >
                {navigationShortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing.sm,
                    }}
                  >
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {shortcut.keys.map((key, keyIndex) => (
                        <span
                          key={keyIndex}
                          style={{ display: 'flex', gap: '4px' }}
                        >
                          <KeyCap keyLabel={key} />
                          {keyIndex < shortcut.keys.length - 1 && (
                            <span
                              style={{
                                color: colors.secondaryText,
                                fontSize: theme.fontSize.xs,
                                alignSelf: 'center',
                              }}
                            >
                              +
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                    <span
                      style={{
                        fontSize: theme.fontSize.sm,
                        color: colors.secondaryText,
                      }}
                    >
                      {shortcut.description}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Window Management Shortcuts */}
            <div>
              <h4
                style={{
                  fontSize: theme.fontSize.sm,
                  fontWeight: '600',
                  marginBottom: theme.spacing.sm,
                  color: colors.secondaryText,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {t('settings.windowManagement')}
              </h4>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: theme.spacing.md,
                }}
              >
                {windowShortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing.sm,
                    }}
                  >
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {shortcut.keys.map((key, keyIndex) => (
                        <span
                          key={keyIndex}
                          style={{ display: 'flex', gap: '4px' }}
                        >
                          <KeyCap keyLabel={key} />
                          {keyIndex < shortcut.keys.length - 1 && (
                            <span
                              style={{
                                color: colors.secondaryText,
                                fontSize: theme.fontSize.xs,
                                alignSelf: 'center',
                              }}
                            >
                              +
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                    <span
                      style={{
                        fontSize: theme.fontSize.sm,
                        color: colors.secondaryText,
                      }}
                    >
                      {shortcut.description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: isMobile ? 'flex-end' : 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: isMobile ? 0 : theme.spacing.xl,
          }}
          onClick={() => setShowLogoutConfirm(false)}
        >
          <div
            style={{
              backgroundColor: colors.primaryBg,
              borderRadius: isMobile ? '16px 16px 0 0' : '8px',
              border: isMobile ? 'none' : `1px solid ${colors.primaryBorder}`,
              borderTop: isMobile ? `1px solid ${colors.primaryBorder}` : undefined,
              padding: isMobile ? '20px 16px' : theme.spacing.xl,
              paddingBottom: isMobile ? 'calc(20px + env(safe-area-inset-bottom))' : theme.spacing.xl,
              maxWidth: isMobile ? '100%' : '400px',
              width: isMobile ? '100%' : '90%',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                fontSize: isMobile ? '18px' : theme.fontSize.lg,
                fontWeight: '600',
                marginBottom: theme.spacing.md,
                color: colors.primaryText,
              }}
            >
              {t('settings.confirmLogout')}
            </h3>
            <p
              style={{
                fontSize: isMobile ? '14px' : theme.fontSize.md,
                color: colors.secondaryText,
                marginBottom: theme.spacing.lg,
              }}
            >
              {t('settings.confirmLogoutMessage')}
            </p>
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: theme.spacing.sm,
                justifyContent: 'flex-end',
              }}
            >
              <Button
                onClick={() => setShowLogoutConfirm(false)}
                variant="secondary"
                size={isMobile ? 'md' : 'sm'}
                style={isMobile ? { width: '100%' } : undefined}
              >
                {t('app.cancel')}
              </Button>
              <Button
                onClick={() => {
                  setShowLogoutConfirm(false)
                  handleLogout()
                }}
                variant="destructive"
                size={isMobile ? 'md' : 'sm'}
                disabled={isLoading}
                isLoading={isLoading}
                style={isMobile ? { width: '100%' } : undefined}
              >
                {t('settings.logoutButton')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* System Settings Section Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.md,
        }}
      >

       {!isMobile && (
         <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, fontSize: theme.fontSize.sm }}>
          ✨
          <Button
            onClick={() => setShowShortcuts(true)}
            variant="secondary"
            size="sm"
          >
            ⌨ {t('settings.keyboardShortcuts')}
          </Button>
          ✨
          </div>
       )}
      </div>

      <span
        style={{
          fontSize: isMobile ? '11px' : theme.fontSize.xs,
          color: colors.secondaryText,
          marginBottom: theme.spacing.xl,
          display: 'block',
        }}
      >
        {t('settings.storageNotice')}
      </span>

      {feedbackMessage && (
        <Message
          variant={feedbackMessage.startsWith('Error:') ? 'error' : 'success'}
          style={{ marginBottom: theme.spacing.lg }}
        >
          {feedbackMessage}
        </Message>
      )}

      {Object.entries(settings).map(([fieldKey, field]) => {
        const translation = getFieldTranslation(fieldKey)
        return (
          <Input
            key={fieldKey}
            id={fieldKey}
            label={translation.label}
            value={field.content}
            onChange={(e) =>
              handleInputChange(
                fieldKey as keyof UserSettings,
                (e.target as HTMLInputElement).value
              )
            }
            placeholder={translation.placeholder}
            disabled={isLoading}
            type={field.type || 'text'}
          />
        )
      })}

      <div
        style={{
          marginTop: theme.spacing.xl,
          textAlign: 'right',
        }}
      >
        <Button onClick={handleSave} disabled={isLoading} isLoading={isLoading}>
          {t('settings.saveSettings')}
        </Button>
      </div>
    </Container>
  )
}

export default function Settings() {
  return (
    <SettingsHelpProvider>
      <SettingsContent />
    </SettingsHelpProvider>
  )
}
